import { inject, Injectable } from '@angular/core';
import {
  deepFlatMap,
  filter,
  NodeType,
  ProductTreeGroupNode,
  ProductTreeNode,
  ProductTreePath,
  ProductTreeService,
  value
} from '@forgerock/backstage-ui-core';
import { TreeNode } from './model/tree-node';

@Injectable({
  providedIn: 'root'
})
export class SearchProductTreeService {
  private productTreeService = inject(ProductTreeService);

  private treeCache = new Map<string, TreeNode<Partial<ProductTreeGroupNode>>[]>();
  private now = new Date().getTime();
  private definition = [ 'familyId', 'productId', 'minorVersion', 'version' ];

  async getTree(eosl: boolean): Promise<TreeNode<Partial<ProductTreeGroupNode>>[]> {
    const key = eosl ? 'eosl' : 'default';

    if (!this.treeCache.has(key)) {
      try {
        const tree = await this.productTreeService.getTreeByDefinition(
          this.definition,
          true,
          false,
          eosl ? null : value(filter('isEosl', false)),
          { '*': [ 'displayValue:asc' ], minorVersion: [ 'minorVersion:desc' ], version: [ 'version:desc' ] }
        ) as ProductTreeGroupNode;

        this.treeCache.set(key, this.traverseTree(tree).items);
      } catch (e) {
        return [];
      }
    }

    return this.treeCache.get(key);
  }

  private traverseTree(node: ProductTreeGroupNode, hide = false): TreeNode<Partial<ProductTreeGroupNode>> {
    const children = node.children?.every(child => child.nodeType === NodeType.group) ?
      node.children.map((it: ProductTreeGroupNode) => this.traverseTree(it, node.children.length <= 1)) : [];

    return {
      id: this.toPath(node, this.definition).toString(),
      title: node.displayValue,
      unselectable: node.type === 'familyId',
      hidden: node.type === 'minorVersion' && hide,
      data: { ...node, children: [] },
      items: children
    }
  }

  private toPath(node: ProductTreeNode, definition: string[]): ProductTreePath {
    const segments: [ string, string ][] = definition.filter(item => !!node[ item ])
      .map(item => [ item, node[ item ] as string ]);

    return new ProductTreePath(segments);
  }

  async getFamilyId(productId: string): Promise<string> | null {
    const product = await this.getProduct(productId);

    return product?.data?.familyId;
  }

  async getLatestVersionTitle(productId: string): Promise<string> | null {
    const product = await this.getProduct(productId);

    return this.getLatestVersion(product).title;
  }

  getLatestVersion(item: TreeNode<Partial<ProductTreeGroupNode>>): TreeNode<Partial<ProductTreeGroupNode>> | null {
    if (item?.items.length) {
      const latest = item.items.find(it => (it.data as ProductTreeGroupNode).artifactCurrent) ?? item.items[ 0 ];
      return this.getLatestVersion(latest);
    } else {
      return item;
    }
  }

  private async getProduct(productId: string): Promise<TreeNode<Partial<ProductTreeGroupNode>>> | null {
    const tree = await this.getTree(true)

    return deepFlatMap(tree, it => it.items)
      .find(it => it.data.productId === productId);
  }

  async getVersion(productId: string, version: string): Promise<ProductTreeGroupNode> | null {
    const product = await this.getProduct(productId);

    return product?.items
      .map(it => it.data as ProductTreeGroupNode)
      .find(it => it.minorVersion === version)
  }

  isEosl(version: ProductTreeGroupNode): boolean {
    if (!version) {
      return false;
    }

    return !!version.eosl && (new Date(version.eosl).getTime() < this.now)
  }
}
